
import { Mail, Shield, TestTube, UserCheck, BarChart3, Lightbulb } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const PhishingSimulation = () => {
  return (
    <LandingPageTemplate
      title="Phishing Simulation & Testing"
      description="Strengthen your human firewall with realistic phishing simulations and targeted training to reduce susceptibility to social engineering attacks."
      keywords={[
        "phishing simulation", 
        "phishing testing", 
        "security awareness", 
        "social engineering", 
        "email security", 
        "security training"
      ]}
      canonicalUrl="/cybersecurity/phishing-simulation-and-testing/"
      schemaData={{
        name: "Phishing Simulation & Testing Service",
        description: "Comprehensive phishing simulation and training program to reduce employee susceptibility to social engineering attacks.",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Advanced"
        highlighted="Phishing Simulation & Testing"
        description="Build resilience against social engineering attacks with realistic phishing simulations and targeted training for your employees."
        ctaText="Schedule a Simulation Demo"
        secondaryButtonText="View Program Details"
        secondaryButtonLink="#features"
        modalTitle="Phishing Simulation Consultation"
        modalDescription="Tell us about your security awareness needs and challenges, and we'll help you design an effective phishing simulation program."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Phishing Simulation",
          service_interest: "Security Awareness"
        }}
        heroPattern="dots"
      />

      <FeatureSection 
        id="features"
        title="Comprehensive Phishing Simulation Program"
        subtitle="HUMAN CYBERSECURITY"
        description="Our phishing simulation service combines realistic testing with targeted training to reduce vulnerability to social engineering attacks."
        background="light"
        features={[
          {
            title: "Realistic Simulations",
            description: "Sophisticated phishing templates that mimic real-world threats targeting your industry and organization.",
            icon: <Mail className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Controlled Testing",
            description: "Safe testing environment with full administrative control over simulation timing, targets, and difficulty.",
            icon: <TestTube className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Targeted Training",
            description: "Immediate education for employees who interact with simulated phishing emails to reinforce secure behavior.",
            icon: <Lightbulb className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Advanced Metrics",
            description: "Comprehensive reporting on susceptibility rates, click-through trends, and security awareness improvement.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk-Based Approach",
            description: "Focused testing for high-risk departments and employees who handle sensitive information or have privileged access.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Behavior Change",
            description: "Program designed to drive lasting behavioral change by reinforcing security awareness over time.",
            icon: <UserCheck className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection 
        title="Our Phishing Simulation Process"
        subtitle="METHODOLOGY"
        description="A structured approach to testing and improving employee resilience to phishing and social engineering attacks."
        steps={[
          {
            title: "Program Design",
            description: "Define program goals, metrics, and campaign schedule based on your organization's risk profile."
          },
          {
            title: "Baseline Assessment",
            description: "Conduct initial simulations to establish baseline susceptibility across the organization."
          },
          {
            title: "Targeted Campaigns",
            description: "Deploy customized phishing campaigns varying in sophistication and social engineering techniques."
          },
          {
            title: "Real-Time Training",
            description: "Deliver immediate education to employees who engage with simulated phishing emails."
          },
          {
            title: "Progress Tracking",
            description: "Monitor improvement with detailed metrics and susceptibility reduction trends."
          },
          {
            title: "Program Refinement",
            description: "Continuously adjust simulation difficulty and content based on employee performance."
          }
        ]}
        layout="vertical"
      />

      <BenefitSection 
        title="Benefits of Phishing Simulation"
        subtitle="SECURITY VALUE"
        background="gradient"
        benefits={[
          {
            title: "Reduced Vulnerability",
            description: "Decrease susceptibility to social engineering attacks through practical experience and training."
          },
          {
            title: "Measurable Results",
            description: "Track improvement over time with detailed metrics on susceptibility rates and behavioral changes."
          },
          {
            title: "Risk Management",
            description: "Identify and address high-risk departments or individuals requiring additional security training."
          },
          {
            title: "Safe Learning Environment",
            description: "Provide employees with realistic phishing experience without actual security risk."
          },
          {
            title: "Compliance Support",
            description: "Meet regulatory requirements for security awareness training with documented testing and results."
          },
          {
            title: "Security Culture",
            description: "Foster a security-conscious culture through ongoing awareness and education."
          }
        ]}
      />

      <CtaSection 
        title="Strengthen Your Human Firewall Against Phishing"
        description="Get started with a customized phishing simulation program designed for your organization's specific needs."
        ctaText="Schedule Your Consultation"
        modalTitle="Phishing Simulation Consultation"
        modalDescription="Let's discuss your organization's phishing risks and design a simulation program that builds resilience against social engineering attacks."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Phishing Simulation",
          service_interest: "Security Awareness"
        }}
      />
    </LandingPageTemplate>
  );
};

export default PhishingSimulation;
